// Beneficiary Manager (§4): gradient header, search, A–Z grouped list, wide
// two-pane layout / narrow master-detail, delete with OTP approval (§4.2).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faChevronRight,
  faPlus,
  faRotate,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import {
  deleteBeneficiary,
  listBeneficiaries,
  requestBeneficiaryOtp,
  resendBeneficiaryOtp,
  verifyBeneficiaryOtp,
} from '@/api/beneficiaries';
import { getCountries } from '@/api/reference';
import { ApiError } from '@/api/client';
import type { Beneficiary, CountryRef } from '@/types/phase2.types';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import Loader from '@/components/ui/Loader/Loader';
import ErrorState from '@/components/ui/ErrorState/ErrorState';
import Button from '@/components/ui/Button/Button';
import OtpSheet from '@/components/common/auth/OtpSheet';
import BeneficiaryDetail from './BeneficiaryDetail';
import './beneficiaries.css';

const groupKey = (alias: string): string => {
  const first = alias.trim().charAt(0).toUpperCase();
  return first >= 'A' && first <= 'Z' ? first : '0-9';
};

const BeneficiaryManager = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isWide = useMediaQuery('(min-width: 720px)');

  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[] | null>(null);
  const [countries, setCountries] = useState<CountryRef[]>([]);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(
    (location.state as { openId?: string } | null)?.openId ?? null
  );
  const [deleteTarget, setDeleteTarget] = useState<Beneficiary | null>(null);
  const [otp, setOtp] = useState<{
    authId: string;
    sessionKey: number;
    destination?: string;
  } | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) {
      setBeneficiaries(null);
      setError('');
    } else {
      setRefreshing(true);
    }
    try {
      const list = await listBeneficiaries();
      setBeneficiaries(list);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load beneficiaries.');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load(true);
    getCountries().then(setCountries).catch(() => setCountries([]));
  }, [load]);

  const filtered = useMemo(() => {
    const list = beneficiaries ?? [];
    const q = query.trim().toLowerCase();
    return list
      .filter((b) => !q || b.alias.toLowerCase().includes(q))
      .sort((a, b) => a.alias.localeCompare(b.alias, undefined, { sensitivity: 'base' }));
  }, [beneficiaries, query]);

  const groups = useMemo(() => {
    const map = new Map<string, Beneficiary[]>();
    for (const b of filtered) {
      const key = groupKey(b.alias);
      if (!map.has(key)) map.set(key, []);
      map.get(key)?.push(b);
    }
    return [...map.entries()].sort(([a], [b]) => {
      if (a === '0-9') return -1;
      if (b === '0-9') return 1;
      return a.localeCompare(b);
    });
  }, [filtered]);

  const selected = useMemo(() => {
    if (!beneficiaries || beneficiaries.length === 0) return null;
    return (
      beneficiaries.find((b) => b.id === selectedId) ??
      (isWide ? beneficiaries[0] : null)
    );
  }, [beneficiaries, selectedId, isWide]);

  const startDelete = async () => {
    const target = deleteTarget;
    if (!target) return;
    try {
      const session = await requestBeneficiaryOtp();
      setOtp({
        authId: session.authId,
        sessionKey: Date.now(),
        destination: session.maskedDestination,
      });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not send the OTP.');
      setOtp(null);
      setDeleteTarget(null);
    }
  };

  const confirmDelete = async (code: string) => {
    if (!otp || !deleteTarget) return;
    await verifyBeneficiaryOtp(otp.authId, code);
    const { alias } = await deleteBeneficiary(deleteTarget.id);
    setOtp(null);
    setDeleteTarget(null);
    setSelectedId(null);
    toast.success(`${alias} deleted.`);
    await load(false);
  };

  const renderList = () => {
    if (!beneficiaries) return <Loader rows={4} />;
    if (error) return <ErrorState message={error} onRetry={() => void load(true)} />;
    if (beneficiaries.length === 0) {
      return (
        <div className="nova-ben-empty">
          <FontAwesomeIcon icon={faUsers} size="3x" className="nova-ben-empty-icon" />
          <p>No beneficiaries yet. Add someone you pay often.</p>
          <Button onClick={() => navigate('/beneficiaries/add')}>
            <FontAwesomeIcon icon={faPlus} /> Add New Beneficiary
          </Button>
        </div>
      );
    }
    if (filtered.length === 0) {
      return <p className="nova-ben-no-match">No beneficiaries match &quot;{query.trim()}&quot;.</p>;
    }
    return (
      <div className="nova-ben-groups">
        {groups.map(([letter, list]) => (
          <div key={letter} className="nova-ben-group">
            <div className="nova-ben-group-heading">{letter}</div>
            {list.map((b) => (
              <button
                type="button"
                key={b.id}
                className={`nova-ben-row-item ${selected?.id === b.id && isWide ? 'active' : ''}`}
                onClick={() => setSelectedId(b.id)}
              >
                <span className="nova-ben-avatar">{b.alias.trim().charAt(0).toUpperCase()}</span>
                <span className="nova-ben-row-text">
                  <span className="nova-ben-alias">{b.alias}</span>
                  <span className="nova-ben-count">
                    {b.accounts.length === 1 ? '1 account' : `${b.accounts.length} accounts`}
                  </span>
                </span>
                <FontAwesomeIcon icon={faChevronRight} className="nova-ben-chevron" />
              </button>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const showDetailPane = isWide ? Boolean(selected) : Boolean(selectedId && selected);
  const listVisible = isWide || !showDetailPane;

  return (
    <div className="nova-ben-page">
      <div className="nova-ben-hero">
        <button
          type="button"
          className="nova-ben-back"
          title="Back"
          aria-label="Back"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/dashboard'))}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <div className="nova-ben-hero-text">
          <h1>Beneficiary Manager</h1>
          <p>Beneficiaries</p>
        </div>
        <button type="button" className="nova-ben-add-btn" onClick={() => navigate('/beneficiaries/add')}>
          <FontAwesomeIcon icon={faPlus} /> Add New Beneficiary
        </button>
      </div>

      <div className="nova-ben-body">
        {listVisible && (
          <div className="nova-ben-list">
            {beneficiaries && beneficiaries.length > 0 && !error && (
              <div className="nova-ben-list-tools">
                <input
                  type="search"
                  className="form-control"
                  placeholder="search by name"
                  aria-label="search by name"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <button
                  type="button"
                  className="nova-icon-btn"
                  title="Refresh"
                  aria-label="Refresh"
                  onClick={() => void load(false)}
                  disabled={refreshing}
                >
                  <FontAwesomeIcon icon={faRotate} spin={refreshing} />
                </button>
              </div>
            )}
            {renderList()}
          </div>
        )}

        {showDetailPane && selected && (
          <div className="nova-ben-detail-pane">
            {!isWide && (
              <button type="button" className="nova-ben-back-list" onClick={() => setSelectedId(null)}>
                <FontAwesomeIcon icon={faArrowLeft} /> All beneficiaries
              </button>
            )}
            <BeneficiaryDetail
              beneficiary={selected}
              countries={countries}
              onEdit={(b) => navigate(`/beneficiaries/edit/${b.id}`)}
              onDelete={(b) => setDeleteTarget(b)}
            />
          </div>
        )}
        {isWide && !showDetailPane && beneficiaries && beneficiaries.length > 0 && (
          <div className="nova-ben-detail-pane nova-ben-detail-placeholder">
            Select a beneficiary to see the details.
          </div>
        )}
      </div>

      {/* Delete confirmation (§4.2) */}
      <Modal show={Boolean(deleteTarget) && !otp} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete {deleteTarget?.alias}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>They&apos;ll be removed from your beneficiaries. This can&apos;t be undone.</Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setDeleteTarget(null)}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={() => void startDelete()}>
            Delete
          </button>
        </Modal.Footer>
      </Modal>

      <OtpSheet
        show={Boolean(otp)}
        destination={otp?.destination}
        sessionKey={otp?.sessionKey}
        onVerify={confirmDelete}
        onResend={async () => {
          if (!otp) return 180;
          const { expiresInSeconds } = await resendBeneficiaryOtp(otp.authId);
          return expiresInSeconds;
        }}
        onClose={() => {
          setOtp(null);
          setDeleteTarget(null);
        }}
      />
    </div>
  );
};

export default BeneficiaryManager;
